import React, { useEffect, useState } from 'react';
import { db } from '../../services/firebase';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { Users, FileText, CheckCircle, AlertTriangle, Target, PlayCircle, UserPlus, Clock } from 'lucide-react';

const StatCard: React.FC<{ title: string, value: string | number, icon: React.ReactNode, color: string }> = ({ title, value, icon, color }) => (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 flex items-center justify-between">
        <div>
            <p className="text-sm font-medium text-gray-500 uppercase">{title}</p>
            <p className="text-2xl font-bold text-gray-800 mt-1">{value}</p>
        </div>
        <div className={`p-4 rounded-full ${color}`}>
            {icon}
        </div>
    </div>
);

const AdminDashboard: React.FC = () => {
    const [stats, setStats] = useState({
        activeUsers: 0,
        totalCases: 0,
        avgPrecision: 0,
        dailyAdLupas: 0
    });
    const [recentLogs, setRecentLogs] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                if (!db) return;

                const usersSnap = await getDocs(collection(db, 'users'));
                const casesSnap = await getDocs(collection(db, 'cases'));

                let activeUsersCount = 0;
                let totalSolved = 0;
                let totalArchived = 0;
                let dailyAdLupasCount = 0;

                const recentUsersList: any[] = [];

                usersSnap.forEach(doc => {
                    const data = doc.data();
                    if (data.status !== 'banned') activeUsersCount++;

                    if (data.adUsesToday) {
                        dailyAdLupasCount += (data.adUsesToday * 3);
                    }

                    if (data.gameState?.archivedCases?.length) {
                        totalArchived += data.gameState.archivedCases.length;
                        totalSolved += data.gameState.archivedCases.filter((c: any) => c.status === 'solved').length;
                    }

                    if (data.createdAt) {
                        recentUsersList.push({
                            id: doc.id,
                            name: data.name || 'Desconhecido',
                            email: data.email,
                            createdAt: data.createdAt.seconds * 1000
                        });
                    }
                });

                recentUsersList.sort((a, b) => b.createdAt - a.createdAt);
                const avgPrecision = totalArchived > 0 ? Math.round((totalSolved / totalArchived) * 100) : 0;

                setStats({
                    activeUsers: activeUsersCount,
                    totalCases: casesSnap.size,
                    avgPrecision: avgPrecision,
                    dailyAdLupas: dailyAdLupasCount
                });
                setRecentLogs(recentUsersList.slice(0, 5));
            } catch (error) {
                console.error("Error fetching admin stats:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, []);

    if (loading) return <div>Carregando estatísticas...</div>;

    return (
        <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-8">Visão Geral</h1>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <StatCard
                    title="Agentes Ativos"
                    value={stats.activeUsers}
                    icon={<Users className="w-6 h-6 text-blue-600" />}
                    color="bg-blue-100"
                />
                <StatCard
                    title="Total de Casos no BD"
                    value={stats.totalCases}
                    icon={<FileText className="w-6 h-6 text-amber-600" />}
                    color="bg-amber-100"
                />
                <StatCard
                    title="Média de Precisão Global"
                    value={`${stats.avgPrecision}%`}
                    icon={<Target className="w-6 h-6 text-green-600" />}
                    color="bg-green-100"
                />
                <StatCard
                    title="Lupas / ADS (Hoje)"
                    value={stats.dailyAdLupas}
                    icon={<PlayCircle className="w-6 h-6 text-indigo-600" />}
                    color="bg-indigo-100"
                />
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Log Administrativo (Últimos Registros)</h2>
                {recentLogs.length > 0 ? (
                    <div className="divide-y divide-gray-100">
                        {recentLogs.map((log, index) => (
                            <div key={index} className="flex items-center justify-between py-3">
                                <div className="flex items-center gap-3">
                                    <div className="bg-green-50 p-2 rounded-full">
                                        <UserPlus size={16} className="text-green-600" />
                                    </div>
                                    <div>
                                        <div className="text-sm font-bold text-gray-800">Novo Agente Recrutado</div>
                                        <div className="text-xs text-gray-500">{log.name} ({log.email})</div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 text-xs text-gray-400 font-mono">
                                    <Clock size={12} /> {new Date(log.createdAt).toLocaleString('pt-BR')}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-sm text-gray-500 text-center py-8">
                        Nenhuma atividade recente registrada.
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminDashboard;
